import { ReactNode, useCallback, useState } from 'react'
import { Box } from '../../../UI/Box'

import { SubTask } from '../../types'
import TaskBody from '../../TaskBody'
import useIsLoggedIn from '../../../../hooks/useIsLoggedIn'
import { selectUser } from '../../../../modules/user/selectors'
import { useGenerateProjectReferralMutation } from '../../../../modules/missionControl/api'
import { copyToClipboard } from '../../../../page_components/Dashboard/Vault'
import { setLithiumAccount } from '../../../../modules/user/slice'
import { ThemedButton } from '../../../ThemedButton'
import AnimatedLoader from '../../../AnimatedLoader'
import { links } from '../../../../router/routes'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Strong } from '../../../Library/Typeography'
import useWeb3 from '../../../../hooks/useWeb3'

export type Props = {
  subtask: SubTask
  taskId: number
  taskAmount: ReactNode
  projectId: number
}

const ReferralTask = ({ subtask, taskId, taskAmount, projectId }: Props) => {
  const { publicName, instructions } = subtask
  const title = publicName ?? 'Validate an on-chain action'
  const description = instructions ?? ''

  const { isLoggedIn } = useIsLoggedIn()
  const { initWeb3 } = useWeb3()
  const { onClick, isLoading, link, copy } = useProjectReferral({
    projectId,
    taskId: subtask.id,
  })

  const [error, setError] = useState<string | null>(null)

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={description}
      error={error}
      extra={taskAmount}
      actions={
        <Box>
          {!isLoggedIn ? (
            <ThemedButton onClick={() => initWeb3()}>
              Connect
            </ThemedButton>
          ) : (
            <Box g={12} dc ac>
              {!!link && <Strong>{link}</Strong>}
              <ThemedButton
                onClick={!!link ? () => copy(link) : onClick}
                $styleType="primary"
              >
                {isLoading ? (
                  <AnimatedLoader />
                ) : !!link ? (
                  'Copy link'
                ) : (
                  'Generate link'
                )}
              </ThemedButton>
            </Box>
          )}
        </Box>
      }
    />
  )
}

function useProjectReferral({ projectId, taskId }) {
  const dispatch = useDispatch()

  const { projectReferrals } = useSelector(selectUser) || {}
  const projectReferral = projectReferrals?.find(
    (referral) => referral.projectId === projectId,
  )
  const [generateReferral, { isLoading }] = useGenerateProjectReferralMutation()

  const copy = useCallback(async (url: string) => {
    try {
      await copyToClipboard(url)
      toast.success('Referral link copied to clipboard!')
    } catch (e) {
      console.error('Error copying to clipboard', e)
    }
  }, [])

  const onClick = useCallback(async () => {
    if (projectReferral?.code) {
      copy(getRealUrlInviteLink(projectReferral.code))
      return
    }
    try {
      const { user } = await generateReferral({ projectId, taskId }).unwrap()
      dispatch(setLithiumAccount(user))
      const referral = user.projectReferrals.find(
        (referral) => referral.projectId === projectId,
      )
      copy(getRealUrlInviteLink(referral.code))
    } catch (e) {
      console.error('Error generating project referral link', e)
      toast.error('There was an error generating your project referral link')
    }
  }, [
    projectReferral?.code,
    copy,
    generateReferral,
    projectId,
    taskId,
    dispatch,
  ])

  const code = projectReferral?.code

  return {
    onClick,
    copy,
    isLoading,
    code,
    link: !!code ? getRealUrlInviteLink(projectReferral?.code) : '',
  }
}

export function getRealUrlInviteLink(code: string) {
  return `${process.env.REACT_APP_FRONT_END_URL}${links.invite(code)}`
}

export default ReferralTask
